export const ZAT = 100_000_000n;

/** zat (bigint or decimal string) → "1.2345" ZEC, trimmed. */
export function zec(z: bigint | string | number, maxDecimals = 4): string {
  const v = typeof z === 'bigint' ? z : BigInt(typeof z === 'number' ? Math.round(z) : z);
  const neg = v < 0n;
  const a = neg ? -v : v;
  const whole = a / ZAT;
  let frac = (a % ZAT).toString().padStart(8, '0').slice(0, maxDecimals).replace(/0+$/, '');
  if (whole === 0n && frac === '' && a > 0n) frac = (a % ZAT).toString().padStart(8, '0').replace(/0+$/, '');
  return (neg ? '-' : '') + groupDigits(whole.toString()) + (frac ? '.' + frac : '');
}

/** Parse a user-typed ZEC amount into zat. Returns null if invalid. */
export function parseZec(s: string): bigint | null {
  const t = s.trim().replace(',', '.');
  if (!/^\d*(\.\d{0,8})?$/.test(t) || t === '' || t === '.') return null;
  const [i, f = ''] = t.split('.');
  return BigInt(i || '0') * ZAT + BigInt((f + '00000000').slice(0, 8));
}

/** Parse tokens (6 decimals) into base units. */
export function parseTokens(s: string, decimals = 6): bigint | null {
  const t = s.trim().replace(',', '.');
  if (!new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`).test(t) || t === '' || t === '.') return null;
  const [i, f = ''] = t.split('.');
  return BigInt(i || '0') * 10n ** BigInt(decimals) + BigInt((f + '0'.repeat(decimals)).slice(0, decimals));
}

export function groupDigits(s: string) {
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Token base units → compact human amount: 12.3M, 845K, 12.5 */
export function tokens(units: bigint | string, decimals = 6): string {
  const v = Number(BigInt(units)) / 10 ** decimals;
  return compact(v);
}

export function tokensExact(units: bigint | string, decimals = 6): string {
  const v = BigInt(units);
  const d = 10n ** BigInt(decimals);
  const frac = (v % d).toString().padStart(decimals, '0').replace(/0+$/, '');
  return (v / d).toString() + (frac ? '.' + frac : '');
}

export function compact(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(a >= 1e10 ? 1 : 2) + 'B';
  if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 1 : 2) + 'M';
  if (a >= 1e3) return (v / 1e3).toFixed(a >= 1e4 ? 1 : 2) + 'K';
  if (a >= 1) return v.toFixed(2).replace(/\.?0+$/, '');
  return v.toPrecision(3);
}

/**
 * Tiny prices with the zero count as a subscript: 0.0₈231 ZEC.
 * Input: zat per whole token.
 */
export function price(zatPerToken: number): { lead: string; zeros: number; digits: string } {
  const zecPerToken = zatPerToken / 1e8;
  if (zecPerToken === 0) return { lead: '0', zeros: 0, digits: '' };
  if (zecPerToken >= 0.001) return { lead: zecPerToken.toPrecision(4), zeros: 0, digits: '' };
  const exp = Math.floor(Math.log10(zecPerToken));
  const zeros = -exp - 1;
  const digits = Math.round(zecPerToken * 10 ** (-exp + 2)).toString().slice(0, 3);
  return { lead: '0.0', zeros, digits };
}

export function pct(x: number) {
  const s = (x * 100).toFixed(Math.abs(x) >= 0.1 ? 0 : 1);
  return (x > 0 ? '+' : '') + s + '%';
}

export function ago(ts: number) {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function short(hex: string, n = 4) {
  return hex.length <= n * 2 + 1 ? hex : `${hex.slice(0, n)}…${hex.slice(-n)}`;
}

export function usd(zat: bigint | string | number, zecUsd: number | null) {
  if (!zecUsd) return null;
  const v = (Number(typeof zat === 'bigint' ? zat : BigInt(String(zat))) / 1e8) * zecUsd;
  return '$' + compact(v);
}
